import { DIALOGUE_DURATION_MS } from '../app/constants';
import { emit } from '../app/events';
import { hasSeenDialogue, markDialogueSeen } from '../app/state';

export interface DialogueContent {
  id: string;
  lines: string[];
  character?: string;
  once?: boolean;
  durationMs?: number;
}

const catalogue = new Map<string, DialogueContent>();
const rotation = new Map<string, number>();

export function registerDialogues(entries: DialogueContent[]): void {
  for (const entry of entries) catalogue.set(entry.id, entry);
}

export function getDialogue(id: string): DialogueContent | undefined {
  return catalogue.get(id);
}

/**
 * Sélection déterministe : on avance d'une réplique à chaque déclenchement.
 * Aucune aléa, donc les tests et le rendu restent reproductibles.
 */
export function nextLine(dialogue: DialogueContent): string {
  if (dialogue.lines.length === 0) return '';
  const index = rotation.get(dialogue.id) ?? 0;
  rotation.set(dialogue.id, (index + 1) % dialogue.lines.length);
  return dialogue.lines[index % dialogue.lines.length] as string;
}

export function resetRotation(): void {
  rotation.clear();
}

export function clearCatalogue(): void {
  catalogue.clear();
  rotation.clear();
}

export interface DialogueBox {
  show(dialogue: DialogueContent): void;
  hide(): void;
  element: HTMLElement;
  destroy(): void;
}

/** Boîte de dialogue légère : trois lignes maximum, fermable au clic ou avec Échap. */
export function createDialogueBox(parent: HTMLElement): DialogueBox {
  const element = document.createElement('div');
  element.className = 'dialogue-box';
  element.hidden = true;

  const text = document.createElement('p');
  text.className = 'dialogue-box__text';
  text.setAttribute('role', 'status');
  text.setAttribute('aria-live', 'polite');

  const speaker = document.createElement('span');
  speaker.className = 'dialogue-box__speaker';
  speaker.hidden = true;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'dialogue-box__close';
  close.setAttribute('aria-label', 'Fermer la réplique');
  close.textContent = '×';

  element.append(speaker, text, close);
  parent.append(element);

  let timer: number | undefined;

  function hide(): void {
    element.hidden = true;
    element.classList.remove('is-visible');
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  }

  function show(dialogue: DialogueContent): void {
    if (dialogue.once && hasSeenDialogue(dialogue.id)) return;
    const line = nextLine(dialogue);
    if (!line) return;

    text.textContent = line;
    if (dialogue.character) {
      speaker.textContent = dialogue.character;
      speaker.hidden = false;
    } else {
      speaker.hidden = true;
    }

    element.hidden = false;
    // Un frame de décalage pour que la transition CSS parte bien de l'état masqué.
    requestAnimationFrame(() => element.classList.add('is-visible'));
    markDialogueSeen(dialogue.id);
    emit('dialogue:shown', { dialogueId: dialogue.id, line });

    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(hide, dialogue.durationMs ?? DIALOGUE_DURATION_MS);
  }

  close.addEventListener('click', hide);
  element.addEventListener('click', (event) => {
    if (event.target === element) hide();
  });

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !element.hidden) {
      hide();
      event.stopPropagation();
    }
  };
  document.addEventListener('keydown', onKeydown);

  return {
    element,
    show,
    hide,
    destroy(): void {
      document.removeEventListener('keydown', onKeydown);
      element.remove();
    },
  };
}
